import React, { useRef } from "react";
import { MoreOutlined } from "us.icons";
import { $Popover, $Button, $Checkbox } from "us.common/components";
import { ItemMenuActions } from "us.collection/components/Workflow/Constants";
import { WorkflowTag } from "us.collection/components/Workflow/Components";
import { EventItemProps } from "us.collection/components/Workflow/Interfaces";
import { getSelectedWorkflows, getEventSelectionItemStyle } from "us.collection/components/Workflow/Functions";
import { connect, ConnectedProps } from "react-redux";
import { ItemMenu } from "../ItemMenu";
import { useDrag, useDrop } from "react-dnd";
import { RootState } from "us.helper/types";
import scrollIntoView from 'scroll-into-view-if-needed'
import { useField } from "formik";
import _ from "lodash";

/**
 * @description - Component for handle event item UI elements
 * @link Design Document - https://unicorn-solutions.atlassian.net/wiki/spaces/USU/pages/3116466232/Manage+Workflow+Events+-+UI
 * @author Ishan Udyoga <ishanud@unicorn-solutions.com>
 * @since 12/9/2024
 */
const EventItem: React.FC<EventItemProps & PropsFromRedux> = (props) => {
  const {
    index,
    popoverOpenId,
    eventRecord,
    isDownloadPopupVisible,
    isSequenceSaving,
    onMove,
    onChangeEventAction,
    onChangeDownloadPopup,
    onChangePopup,
  } = props;

  const ref = useRef<any>(null);
  const [isDraggableField] = useField("isDraggable");
  const [selectionEnabledCategory] = useField("selectionEnabledCategory");
  const [selectedWorkflows, ,selectedWorkflowsHelper] = useField("selectedWorkflows");

  const { id, workflowType, isDomainViewActive, displayName, adapterName, workflowGroupStateId } =
    eventRecord ?? {};

  const dragId = "ITEM_" + workflowType;
  const isDraggable = isDraggableField?.value && !isSequenceSaving;

  const [, drag] = useDrag({
    type: dragId,
    item: { index, type: dragId, id: id },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: isDraggable,
  });

  const [{ canDrop, isOver }, drop] = useDrop({
    accept: dragId,
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
      canDrop: !!monitor.canDrop(),
    }),
    drop(item: any) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      onMove(dragIndex, hoverIndex, workflowType);

      setTimeout(() => {
        const scrollElement = document.getElementById("ws-tile-" + item?.id);
        if (scrollElement) {
          
           scrollIntoView(scrollElement, {
            scrollMode: 'if-needed',
            block: 'start',
            inline: 'nearest',
            behavior: 'smooth'
          });

          scrollElement.style.backgroundColor = '#f3fff4';
          setTimeout(() => {
              scrollElement.style.backgroundColor = "";
          }, 2000);
        }
       }, 100);

      item.index = hoverIndex;
    },
  });

  drag(drop(ref));
  const isDropEnabled = isOver && canDrop;

  return (
    <div ref={ref} id={"ws-tile-"+id} className={`ws-tile ${isDraggable ? "ws-tile-drag" : ""}`}>
       {selectionEnabledCategory?.value == workflowType && (
          <div className="we-select-all">
            <$Checkbox
              name={"event-" + id}
              checked={_.some(selectedWorkflows.value, { workflowGroupStateId })}
              className="mr-3"
              onChange={(e: any) => {
                selectedWorkflowsHelper.setValue(
                  getSelectedWorkflows(
                    selectedWorkflows.value,
                    eventRecord,
                    e.target.checked,
                    "event"
                  )
                );
              }}
            />
            </div>
          )}
      <div className="we-item" style={getEventSelectionItemStyle(selectionEnabledCategory?.value, workflowType)}>
        <div
          className="d-flex flex-grow-1 align-items-start"
          style={{
            transform: isDropEnabled ? "translateX(10px)" : "unset",
            opacity: isDropEnabled ? 0.5 : "unset",
            transition: "all 0.3s ease"
          }}
        >
         
          <WorkflowTag
            isEvent={true}
            workflowType={workflowType}
            activeStatus={isDomainViewActive}
          />
          <div className="we-item-content">
            <div className="we-item-name">{displayName}</div>
            <small className="we-item-da">{adapterName}</small>
          </div>
        </div>
        <div>
          <$Popover
            placement="rightTop"
            content={
              <ItemMenu
                isDownloadPopupVisible={isDownloadPopupVisible}
                onCallAction={(action: ItemMenuActions) =>
                  onChangeEventAction(action, eventRecord)
                }
                onCancelChildPopup={() => {
                  onChangePopup(-1);
                  onChangeDownloadPopup(false);
                }}
              />
            }
            destroyTooltipOnHide
            trigger="click"
            open={id == popoverOpenId}
            onOpenChange={(visible: boolean) => {
              onChangePopup(visible ? id : -1);
              onChangeDownloadPopup(false);
            }}
          >
            <$Button
              type="link"
              icon={<MoreOutlined />}
              size="small"
              onClick={() => onChangePopup(id)}
            />
          </$Popover>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => {
  const { Workflow } = state;
  const { isSequenceSaving } = Workflow;
  return {
    isSequenceSaving,
  };
};

const connector = connect(mapStateToProps, {});

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(EventItem);
